
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount,
  ref,
  Ref,
  watch
} from "vue";
import {
  Course,
  CourseCertificate,
  Instructor,
  UserCourse
} from "../../../../domain/entities/entities";
import { Content } from "../content/helpers";
import {
  dispatchSuccessBox,
  formattedDuration,
  isCirculoTemplate,
  isDecolaTemplate,
  scrollSmoothToElement,
  scrollToTop
} from "../../components/mixins";
import Accordion from "../../components/application/Accordion/Accordion.vue";
import AccordionItem from "../../components/application/Accordion/AccordionItem.vue";
import router from "../../../../src/routes";
import { wrapLoading } from "../../utils/wrap_loading";
import ModalIntroductoryContent from "../../components/application/ModalIntroductoryContent.vue";
import VideoPlayButton from "../../components/application/buttons/VideoPlay.vue";
import { dispatchError } from "../../utils/error_handling";
import { useStore } from "vuex";
import TUIViewer from "../../components/application/TUIViewer.vue";
import FakeIframe from "../../components/application/FakeIframe.vue";
import CustomDropdown from "../../components/application/modals/CustomDropdown.vue";
import RecommendCourse from "../../components/modals/RecommendCourse.vue";
import { useRoute } from "vue-router";
import { addCertificateToLinkedIn } from "./linkedinUtils";

export default defineComponent({
  methods: { isCirculoTemplate },
  components: {
    FakeIframe,
    TUIViewer,
    AccordionItem,
    Accordion,
    ModalIntroductoryContent,
    VideoPlayButton,
    CustomDropdown,
    RecommendCourse
  },
  props: {
    course_id: {
      required: true,
      type: String
    }
  },

  setup(props) {
    const store = useStore();
    const route = useRoute();

    const loadCourse = async () => {
      try {
        await wrapLoading(async () => {
          scrollToTop();
          await store.dispatch(
            "course_module/loadCourse",
            Number(props.course_id)
          );
          await store.dispatch(
            "course_module/userCourseSituation",
            Number(props.course_id)
          );
          await store.dispatch(
            "course_module/loadContents",
            Number(props.course_id)
          );
          await loadInstructors(parseInt(props.course_id));
          showSeeMoreButton();
        });
      } catch (e) {
        console.error("[loadCourse] Error ", e);
        await dispatchError(e);
      }
    };

    onBeforeMount(() => {
      loadCourse();
    });

    const user = store.getters["userProfile_module/user"];

    const dropdownItems = computed(() => {
      const items = [
        {
          label: "Sair do treinamento",
          description:
            'Não ver mais esse treinamento na dashboard e em "Meus treinamentos" até iniciá-lo novamente',
          action: leaveCourse,
          disabled: !(userCourse.value.enrolled && !userCourse.value.finished),
          icon: require("../../assets/static/icons/block.svg")
        }
      ];

      const allowedUserTypes = [2, 4, 5];

      if (allowedUserTypes.includes(user.userType)) {
        items.push({
          label: "Recomendar treinamento",
          description: "Recomendar esse treinamento para um membro do seu time",
          action: async () => getIndicationUsersFromModal(),
          disabled: false,
          icon: require("../../assets/static/icons/suggestion.svg")
        });
      }

      const isFav = course.value.isFavorite;
      items.push({
        label: isFav ? "Remover dos favoritos" : "Favoritar",
        description: isFav
          ? "Remover este treinamento dos favoritos"
          : 'Favoritar este treinamento, ele aparecerá nos treinamentos favoritos no "Meu dashboard"',
        action: isFav
          ? async () => await removeFavoriteTraining(course.value.id)
          : async () => await addFavoriteTraining(course.value.id),
        disabled: false,
        icon: isFav
          ? require("../../assets/static/icons/broken-heart.svg")
          : require("../../assets/static/icons/heart.svg")
      });

      return items;
    });

    const dropdownCertificateItems = computed(() => {
      return [
        {
          label: "Baixar certificado",
          description: "Baixe o certificado em seu dispositivo",
          action: () => openCertificate(course.value),
          disabled: false,
          icon: require("../../assets/static/icons/certificate-blue.svg")
        },
        {
          label: "Adicionar ao LinkedIn",
          description:
            "Redirecione-se ao LinkedIn para adicionar o certificado",
          action: async () => await addToLinkedIn(course.value),
          disabled: false,
          icon: require("../../assets/static/icons/linkedin.svg")
        }
      ];
    });

    const addToLinkedIn = async (course: Course): Promise<void> => {
      try {
        await wrapLoading(async () => {
          await store.dispatch("course_module/loadCertificate", course.id);
          const certificate: CourseCertificate =
            store.getters["course_module/certificate"];

          const issueDate = new Date(course.statusCourse.finishedAt);

          const certificateData = {
            name: course.title,
            certificateUrl: certificate.path,
            certificateId: course.id.toString(),
            issueYear: issueDate.getFullYear(),
            issueMonth: issueDate.getMonth() + 1
          };

          await addCertificateToLinkedIn(certificateData);
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    async function getIndicationUsersFromModal() {
      course.value.id = await store.dispatch("list_picker_module/open", {
        modal: "IndicationUsersModal",
        selected: course.value.id,
        allowMultiple: true
      });
    }

    const isIntroductoryContentModalOpen: Ref<boolean> = ref(false);

    const course: ComputedRef<Course> = computed(
      (): Course => {
        const course = store.getters["course_module/course"];
        course.duration = formattedDuration(course.duration);
        return course;
      }
    );

    const userCourse: ComputedRef<UserCourse> = computed(
      (): UserCourse => store.getters["course_module/userCourseSituation"]
    );

    const contents: ComputedRef<Content[]> = computed(
      (): Content[] => store.getters["course_module/contents"]
    );

    const instructors: ComputedRef<Instructor[]> = computed(
      () => store.getters["course_module/instructors"]
    );

    const selectedInstructorIndex: Ref<number> = ref(-1);

    const showOptions = ref(false);

    const isRecommendOpened: Ref<boolean> = ref(false);

    const recommendCourse = () => {
      isRecommendOpened.value = true;
    };

    const closeRecommendModal = () => {
      isRecommendOpened.value = false;
    };

    /** Enroll or resume course based on the current course's state. */
    const onClick = async (course: Course): Promise<void> => {
      try {
        if (!userCourse.value.enrolled) await enroll(course);
        await redirectToContent();
      } catch (e) {
        await dispatchError(e);
      }
    };

    /** Enroll user on the course. */
    const enroll = async (course: Course): Promise<void> => {
      await store.dispatch("course_module/enrollInCourse", course.id);
    };

    /** Redirects the user to the first content_provider that is not finished */
    async function redirectToContent(): Promise<void> {
      const notFinishedContents = contents.value.filter(content => {
        if (content.status.progress !== 100 && !content.isBlocked) {
          return content;
        }
      });
      const currentContent = notFinishedContents.length
        ? notFinishedContents[0]
        : contents.value[0];
      await router.push({
        name: "course_content",
        params: { content_id: currentContent.id }
      });
    }

    /** Dispatch an action that require the certificate path and redirect to the path. */
    const openCertificate = (course: Course): void => {
      wrapLoading(async () => {
        try {
          await store.dispatch("course_module/loadCertificate", course.id);
          const certificate: CourseCertificate =
            store.getters["course_module/certificate"];
          window.open(certificate.path, "_blank");
        } catch (e) {
          await dispatchError(e);
        }
      }, "Gerando certificado...");
    };

    /** Scroll to the selected instructor. */
    const goToInstructor = (index: number): void => {
      const data = index
        ? `[data-instructor-index='${index - 1}']`
        : `[data-instructors]`;
      scrollSmoothToElement(data);
      setSelectedInstructor(index);
    };

    watch(
      () => route.params.course_id,
      () => {
        loadCourse();
      }
    );

    const setSelectedInstructor = (index: number): void => {
      selectedInstructorIndex.value = index;
      setTimeout(() => {
        selectedInstructorIndex.value = -1;
      }, 2000);
    };

    function seeMoreDescription(index: number): void {
      const buttonReadMore = document.querySelector(
        `[data-see-more="${index}"]`
      ) as HTMLButtonElement;
      const biography = document.querySelector(
        `[data-biography="${index}"]`
      ) as HTMLParagraphElement;

      if (buttonReadMore && biography) {
        if (buttonReadMore.innerText === "ver mais") {
          buttonReadMore.innerText = "ver menos";
          biography.classList.remove("dots");
        } else {
          buttonReadMore.innerText = "ver mais";
          biography.classList.add("dots");
        }
      }
    }

    function showSeeMoreButton(): void {
      if (!instructors.value) return;
      instructors.value.forEach((instructor: Instructor, index: number) => {
        const buttonReadMore = document.querySelector(
          `[data-see-more="${index}"]`
        ) as HTMLButtonElement;
        const biography = document.querySelector(
          `[data-biography="${index}"]`
        ) as HTMLParagraphElement;

        if (biography && biography.offsetHeight > 48) {
          buttonReadMore.style.display = "flex";
          biography.classList.add("dots");
        } else if (biography && buttonReadMore) {
          buttonReadMore.style.display = "none";
        }
      });
    }

    const loadInstructors = async (courseID: number) => {
      try {
        await wrapLoading(async () => {
          await store.dispatch("course_module/loadInstructors", courseID);
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const addFavoriteTraining = async (courseID: number) => {
      try {
        await wrapLoading(async () => {
          await store.dispatch("course_module/addFavoriteTraining", courseID);
          course.value.isFavorite = true;
          await dispatchSuccessBox("Treinamento favoritado!");
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const removeFavoriteTraining = async (courseID: number) => {
      try {
        await wrapLoading(async () => {
          await store.dispatch(
            "course_module/removeFavoriteTraining",
            courseID
          );
          course.value.isFavorite = false;
          await dispatchSuccessBox("Treinamento removido dos favoritos!");
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const onClickContent = async (content: Content) => {
      try {
        if (!userCourse.value.enrolled) {
          await enroll(course.value);
        }

        if (content.isBlocked) {
          return;
        }

        if (!content.isReleased) {
          return;
        }

        await router.push({
          name: "course_content",
          params: { course_id: course.value.id, content_id: content.id }
        });
      } catch (e) {
        console.log("[onClickContent] Error ", e);
        await dispatchError(e);
        throw e;
      }
    };

    const leaveCourse = async () => {
      try {
        showOptions.value = false;
        await wrapLoading(async () => {
          await store.dispatch("course_module/leaveCourse", course.value.id);
          await store.dispatch(
            "course_module/loadCourse",
            Number(props.course_id)
          );
          await store.dispatch(
            "course_module/userCourseSituation",
            Number(props.course_id)
          );
        });

        await dispatchSuccessBox("Você saiu do treinamento com sucesso!");
      } catch (e) {
        await dispatchError(e);
      }
    };

    return {
      course,
      userCourse,
      contents,
      instructors,
      selectedInstructorIndex,
      isIntroductoryContentModalOpen,
      showOptions,
      formattedDuration,
      onClick,
      openCertificate,
      goToInstructor,
      seeMoreDescription,
      onClickContent,
      isDecolaTemplate,
      leaveCourse,
      dropdownItems,
      isRecommendOpened,
      closeRecommendModal,
      dropdownCertificateItems
    };
  }
});
